import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { SiteBranding } from '../api/site';

export type BrandPreview = Partial<Pick<
  SiteBranding,
  'header_logo_height' | 'footer_logo_height' | 'title_font_size' | 'title_text_transform' | 'title_bold' | 'title_italic'
>>;

interface BrandPreviewContextValue {
  preview: BrandPreview;
  setPreview: (next: BrandPreview) => void;
  clearPreview: () => void;
}

const BrandPreviewContext = createContext<BrandPreviewContextValue>({
  preview: {},
  setPreview: () => {},
  clearPreview: () => {},
});

/**
 * Live-preview channel for `BrandStyleModal`'s logo-height/title-style
 * fields. The modal is a sibling of `SiteHeader`/`SiteFooter` (mounted from
 * `SiteAdminChrome`), not a parent, so there's no shared component state to
 * drag a slider into — this context is that missing link. `preview` values
 * take precedence over the saved branding wherever the header/footer render
 * them; the modal clears it on close/unmount, so leaving without saving
 * reverts the page to the last-committed values (Save is what makes a value
 * stick — see docs in BrandStyleModal).
 */
export const BrandPreviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preview, setPreview] = useState<BrandPreview>({});
  const clearPreview = useCallback(() => setPreview({}), []);
  const value = useMemo(() => ({ preview, setPreview, clearPreview }), [preview, clearPreview]);
  return <BrandPreviewContext.Provider value={value}>{children}</BrandPreviewContext.Provider>;
};

export const useBrandPreview = (): BrandPreview => useContext(BrandPreviewContext).preview;

export const useSetBrandPreview = () => {
  const { setPreview, clearPreview } = useContext(BrandPreviewContext);
  return { setPreview, clearPreview };
};
